import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Typography
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchBox from "components/SearchBox";
import useLoadMore from "hooks/useLoadMore";
import { ClearIcon } from "@mui/x-date-pickers";
import VendorTable from "../components/TableVendorList";
import { MstVendorDetail, Vendor } from "../types/vendor";
import useVendorList from "../hooks/useVendorList";
import VendorFormModal from "../components/VendorFormModal";
import { SEND_PATTERN_OPTIONS } from "../constants/sendPattern";

/**
 * VendorList component displays a list of vendors with search and filter options.
 *
 * Features:
 * - Search by vendor code or name.
 * - Filter by send pattern and status flag.
 * - Displays total count of vendors.
 * - Supports loading more vendors.
 * - Allows editing vendor details via a modal.
 *
 * Uses:
 * - useVendorList: Custom hook for vendor data and actions.
 * - useLoadMore: Custom hook for infinite loading.
 * - Material-UI components for UI.
 */
function VendorList() {
  const navigate = useNavigate();
  const {
    vendors,
    queryParams,
    setQueryParams,
    handleSearch,
    onUpdateVendor,
    total,
    onLoadMore,
    loading,
  } = useVendorList();
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [isShowModal, setIsShowModal] = useState(false);

  const { renderNodeLoader } = useLoadMore(onLoadMore, loading);

  const openModal = (vendor: Vendor) => {
    setSelectedVendor(vendor);
    setIsShowModal(true);
  };

  const handleCloseModal = () => {
    setIsShowModal(false);
    setSelectedVendor(null);
  };

  const handleSubmitUpdateVendor = async (vendor: MstVendorDetail) => {
    await onUpdateVendor(vendor);
    handleCloseModal();
  };

  const handleClear = () => {
    if (queryParams.send_pattern !== undefined && queryParams.send_pattern !== null) {
      delete queryParams.send_pattern;
      setQueryParams({ ...queryParams });
    }
  };

  return (
    <>
      <Box sx={{ px: { xs: 2.5, md: 3, lg: "60px" }, pt: { xs: 2, md: 2.5, lg: 3 } }}>
        <Button onClick={() => navigate(-1)} variant="custom-outlined">
          <Box>＜ 戻る</Box>
        </Button>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", px: { xs: 2.5, md: 3, lg: "60px" } }}>
        <Box
          sx={{
            mt: { xs: 3, md: 4, lg: "46px" },
            textAlign: "left",
            width: { xs: "100%", lg: "15%" },
          }}
        >
          <Typography
            variant="h4"
            margin={0}
            textAlign="left"
            sx={{ fontSize: { xs: 24, lg: 33 }, color: "var(--gray-700)" }}
          >
            マスタ管理
          </Typography>
          <Typography
            variant="h5"
            margin={0}
            textAlign="left"
            sx={{ fontSize: { xs: 24, lg: 28 }, color: "var(--gray-700)", whiteSpace: "nowrap" }}
          >
            仕入先一覧
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
            mt: { xs: 3, md: 4, lg: 9 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              columnGap: "35px",
              margin: "0 auto",
              marginBottom: "20px",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <SearchBox
                title="仕入先コード/仕入先名"
                id="vendor_cd_or_name"
                placeholder=""
                value={queryParams.vendor_cd_or_name}
                onChangeText={(value) =>
                  setQueryParams({ ...queryParams, vendor_cd_or_name: value })
                }
                sxTextField={{ width: { sm: "100%", xl: "239px" } }}
                sx={{ minWidth: 0, width: { xs: "100%", lg: "auto" } }}
                sxLabel={{ width: { xs: "230px", lg: "auto" }, px: 3 }}
              />
              <SearchBox
                title="送信パターン"
                id="send_pattern"
                placeholder=""
                sxLabel={{ width: { xs: 258, lg: 135 }, maxWidth: 258 }}
                sx={{ minWidth: 0, width: { xs: "100%", lg: "auto" } }}
                fieldWidth="100%"
              >
                <FormControl
                  sx={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "center",
                    width: "100%",
                    flexDirection: "row",
                  }}
                >
                  <Select
                    id="send_pattern"
                    value={
                      queryParams.send_pattern !== undefined && queryParams.send_pattern !== null
                        ? queryParams.send_pattern
                        : ""
                    }
                    onChange={(event) =>
                      setQueryParams({ ...queryParams, send_pattern: Number(event.target.value) })
                    }
                    sx={{
                      width: { xs: "100%", lg: "239px" },
                      height: "44px",
                      fontSize: { xs: 16, md: 18, lg: 20 },
                    }}
                  >
                    {SEND_PATTERN_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {queryParams.send_pattern !== undefined && queryParams.send_pattern !== null && (
                    <IconButton onClick={handleClear} size="small">
                      <ClearIcon />
                    </IconButton>
                  )}
                </FormControl>
              </SearchBox>
            </Box>
            <FormControlLabel
              sx={{
                fontSize: "23px",
                color: "var(--black)",
                ml: "13px",
              }}
              control={
                <Checkbox
                  checked={queryParams.status_flag === 1}
                  color="secondary"
                  onChange={() =>
                    setQueryParams({ ...queryParams, status_flag: queryParams.status_flag ? undefined : 1 })
                  }
                />
              }
              label={
                <Typography sx={{ fontSize: "18px", color: "var(--black)" }}>
                  有効のみ
                </Typography>
              }
            />
          </Box>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "var(--gray-500)",
              color: "var(--white)",
              width: "190px",
              height: "50px",
              fontSize: "25px",
              my: 2.5,
              borderRadius: "9px",
              ":hover": {
                backgroundColor: "var(--gray-250)",
                boxShadow: "var(--shadow)",
              },
            }}
            onClick={handleSearch}
          >
            検索
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          backgroundColor: "var(--offwhite-100)",
          p: 2.5,
        }}
      >
        <Box
          sx={{
            width: { lg: "15%" },
            display: { xs: "none", lg: "block" },
          }}
        >
          &nbsp;
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          paddingBottom="20px"
          flex={1}
          sx={{ width: "100%" }}
        >
          <Box
            display="flex"
            justifyContent="end"
            fontSize="20px"
            marginBottom="15px"
            sx={{ maxWidth: "95vw", width: "1250px" }}
          >
            {`件数【${total}件】`}
          </Box>
          <Box sx={{ overflowX: "auto", width: { xs: "100%", lg: "auto" } }}>
            <VendorTable vendors={vendors} openModal={openModal} />
            {renderNodeLoader()}
          </Box>
        </Box>
      </Box>
      {selectedVendor && (
        <VendorFormModal
          visible={isShowModal}
          onClose={handleCloseModal}
          onHandleSubmit={handleSubmitUpdateVendor}
          isLoading={loading}
          vendorDetail={selectedVendor}
        />
      )}
    </>
  );
}

export default VendorList;
